import { format, isAfter, isBefore, parseISO, isToday, isTomorrow } from 'date-fns';

import { localeCodes } from './constants/locales';

const DEFAULT_LOCALE = localeCodes.enUS;

export const isDateBetween = (date: Date | number, start: Date | number, end: Date | number) =>
    isAfter(date, start) && isBefore(date, end);

export const formatTime = (
    date: Date | number,
    locale = DEFAULT_LOCALE,
    options: Intl.DateTimeFormatOptions = {}
): string => {
    return new Date(date).toLocaleTimeString(locale, options);
};

/**
 *
 * @param date date object, or timestamp, or a string in a format recognized by the Date.parse
 */
export const formatTimeShort = (
    date: Date | number | string,
    locale = DEFAULT_LOCALE,
    options: Intl.DateTimeFormatOptions = {}
): string =>
    new Date(date).toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit', ...options });

export const formatShortDateWithDots = (
    date: Date | number,
    locale = DEFAULT_LOCALE,
    options: Intl.DateTimeFormatOptions = {}
): string => {
    const localizedDate = new Date(date).toLocaleDateString(locale, {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
        ...options,
    });

    return localizedDate
        .replace(/\s/g, '')
        .split(/(\d{2})./)
        .filter(Boolean)
        .join('.');
};

type FormatDateParams = {
    date: Date | number;
    mask?: string;
    locale?: string;
    options?: Intl.DateTimeFormatOptions;
};

/**
 * Formats date by a given mask or locale and according to current locale.
 */
export const formatDate = ({
    date,
    mask,
    locale = DEFAULT_LOCALE,
    options = {},
}: FormatDateParams): string => {
    const newDate = new Date(date);

    if (mask) return format(newDate, mask);

    return newDate.toLocaleString(locale, {
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
        ...options,
    });
};

export const formatDateAndTimeForRecordingTime = (
    date: Date | number,
    locale = DEFAULT_LOCALE,
    options: Intl.DateTimeFormatOptions = {}
): string =>
    new Date(date).toLocaleString(locale, {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        ...options,
    });

const zeroPad = (number: number) => String(number).padStart(2, '0');

/**
 * HH:MM:SS
 * HHH:MM:SS
 * HHHH:MM:SS
 * As a duration is not a date and here it has specific format (days accounted as hours also), we decided
 * to keep it as is. And do not use i18n.t('global.dateFormats.*') for duration.
 */
export const extendedTimeFormat = (ms: number): string => {
    const d = new Date(ms);
    const days = d.getUTCDate() - 1;
    const hours = d.getUTCHours();
    const minutes = d.getUTCMinutes();
    const seconds = d.getUTCSeconds();

    return [days * 24 + hours, minutes, seconds].map(zeroPad).join(':');
};

export const getCurrentTimestamp = (): number => Date.now();

/**
 * check is timestamp was later than current date timestamp
 * @param time
 */
export const checkLaterThanNow = (time: number): boolean => time > getCurrentTimestamp();

const localeCapitalize =
    (locale = DEFAULT_LOCALE) =>
    (string: string) =>
        string.charAt(0).toLocaleUpperCase(locale) + string.slice(1);
const TODAY_CODE = 0;
const TOMORROW_CODE = 1;

/**
 * Returns text name of the day of week or localized "today"/"tomorrow" if replace flag is active + date in format month and day.
 */
export const getDayOfTheWeek = (
    dateISO: string,
    locale = DEFAULT_LOCALE,
    replaceToday = false,
    options: Intl.DateTimeFormatOptions = {}
): string => {
    const capitalize = localeCapitalize(locale);
    const parsedDate = parseISO(dateISO);
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    const dateFormatOptions = { month: 'numeric', day: 'numeric' };

    if (replaceToday && isToday(parsedDate)) return capitalize(rtf.format(TODAY_CODE, 'day'));

    if (replaceToday && isTomorrow(parsedDate)) {
        const shortWeekDayOptions = { weekday: 'short' } as const;
        const shortWeekDay = parsedDate.toLocaleString(locale, shortWeekDayOptions);
        const shortWeekDayWithDate = parsedDate.toLocaleString(locale, {
            ...shortWeekDayOptions,
            ...dateFormatOptions,
            ...options,
        } as Intl.DateTimeFormatOptions);
        const tomorrow = capitalize(rtf.format(TOMORROW_CODE, 'day'));

        return shortWeekDayWithDate.replace(shortWeekDay, tomorrow);
    }

    const weekDayWithDate = parsedDate.toLocaleString(locale, {
        weekday: 'long',
        ...dateFormatOptions,
        ...options,
    } as Intl.DateTimeFormatOptions);

    return capitalize(weekDayWithDate);
};

export const parseTimestamp = (str: string): number | null =>
    str ? new Date(str).valueOf() : null;
